<template>
    <div class="vx-server-side-set">
        <div class="flex flex-nowrap justify-between items-end">
            <div class="flex flex-nowrap items-end">
                <page-size-select
                    v-if="!fixedPageSize"
                    v-model="pageSize"
                    :disabled="disabled"
                    class="mr-4"
                />
                <csv-button
                    v-if="hasFetchCsv"
                    :endpoint="fetchCsv"
                    :filters="parameters"
                    class="mr-4"
                    :current-results="activeDataSet.data"
                    :total-results="activeDataSet.count"
                />
                <div class="flex flex-grow items-end">
                    <slot name="header-inner" />
                </div>
            </div>
            <div v-if="enableSearch" class="con-input-search vs-table--search">
                <vx-input
                    v-model="searchText"
                    icon="icon-search"
                    type="text"
                    remove-validation-padding
                    :disabled="disabled"
                />
            </div>
        </div>
        <vx-divider />
        <div ref="results" class="relative w-full">
            <transition-group
                appear
                name="set-item"
                tag="div"
                class="flex flex-wrap w-full"
                :style="containerStyle"
                @before-leave="beforeLeave"
            >
                <div
                    v-for="(entry, index) in data"
                    :key="getKey(entry)"
                    :style="getCellStyles(index)"
                    class="entry-cell"
                >
                    <slot name="entry" :entry="entry" :index="index">
                        <vx-card title="MUST OVERRIDE SLOT: entry" subtitle="......... please?">
                            {{ entry }}
                        </vx-card>
                    </slot>
                </div>
            </transition-group>
        </div>
        <vx-pagination
            v-if="!disabled"
            class="mt-6"
            :total-pages="totalPages"
            v-model="currentPage"
        />
    </div>
</template>

<script>
import CsvButton from '../../../components/generic/VxServerSideTable/CsvButton.vue'
import PageSizeSelect from '../VxServerSideTable/PageSizeSelect.vue'

export default {
    name: 'vx-server-side-set',
    components: { CsvButton, PageSizeSelect },
    props: {
        value: {
            type: Object
        },
        idKey: {
            type: [Function, String],
            default: 'id'
        },
        fixedPageSize: {
            type: [Number, String],
            required: false
        },
        filters: {
            type: Object,
            default: () => {}
        },
        fetchData: {
            type: Function,
            required: true
        },
        fetchCsv: {
            type: Function,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        columnCount: {
            type: [Number, String],
            default: 4
        },
        gap: {
            type: Number,
            default: 4
        },
        enableSearch: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            sortParams: {
                sortKey: null,
                sortType: null
            },
            onActiveRoute: true,
            searchTimeout: null,
            currentPage: 1,
            searchText: null,
            sortKey: null,
            sortType: null,
            pageSize: 15,
            typingDelay: 1000,
            parameterHash: 'initial',
            transitionDelayMultiplier: 0.05,
            pageSizeOptions: [
                { text: '10', value: 10 },
                { text: '15', value: 15 },
                { text: '50', value: 50 },
                { text: '100', value: 100 },
                { text: '200', value: 200 }
            ],
            cachedData: {
                initial: {
                    error: false,
                    loading: true,
                    pages: 1,
                    count: 0,
                    data: []
                }
            }
        }
    },
    watch: {
        parameters: {
            handler: function (val, oldV) {
                // if (val.startDate && val.endDate) {
                this.getData(val)
                // }
            },
            immediate: true,
            deep: true
        },
        pageResetParameters: {
            handler: function (val, oldV) {
                this.currentPage = 1
            },
            deep: true
        },
        isLoadingActiveSet: {
            handler: function (val) {
                this.setDataLoadSpinner(val)
            },
            immediate: true
        },
        isActive(newV, oldV) {
            if (newV === true) {
                this.getData(this.parameters)
            }
        },
        data: {
            handler: function (newV) {
                this.$emit('page-results-changed', newV)
            },
            deep: true
        }
    },
    computed: {
        containerStyle() {
            var gapInRem = this.gap * 0.25
            return {
                gridGap: `${gapInRem}rem`,
                gap: `${gapInRem}rem`
            }
        },
        cellStyles() {
            return {
                width: `calc(${100 / this.columnCount}% - ${
                    this.totalGapWidthInRem / this.columnCount
                }rem)`
            }
        },
        containerStyles() {
            return {
                gridGap: `${this.gapWidthInRem}rem`
            }
        },
        gapWidthInRem() {
            return this.gap * 0.25
        },
        totalGapWidthInRem() {
            return this.gapWidthInRem * (this.columnCount - 1)
        },
        localValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        hasFetchCsv() {
            return !!this.fetchCsv
        },
        isActive() {
            return this.onActiveRoute && !this.disabled
        },
        pageSize_x() {
            return this.fixedPageSize || this.pageSize
        },
        activeDataSet() {
            return this.cachedData[this.parameterHash]
        },
        getKey() {
            if (typeof this.idKey === 'function') {
                return this.idKey
            }
            return (row, index) => {
                return row[this.idKey] || index + this.currentPage * this.pageSize_x
            }
        },
        data() {
            return this.activeDataSet.data
        },
        totalPages() {
            return this.activeDataSet.pages || 1
        },
        localFilters() {
            return {
                search: this.searchText,
                ...this.filters
            }
        },
        pageResetParameters() {
            return {
                ...this.localFilters,
                pageSize: this.pageSize_x
            }
        },
        parameters() {
            return {
                ...this.pageResetParameters,
                sortKey: this.sortParams.sortKey || this.pageResetParameters.sortKey,
                sortType: this.sortParams.sortType || this.pageResetParameters.sortType,
                pageNo: this.currentPage - 1 // 0 indexed on server side
            }
        },
        isLoadingActiveSet() {
            return !this.disabled && this.activeDataSet.loading
        },
        attrs() {
            var result = { ...this.$attrs, disabled: this.disabled }
            return result
        },
        rowCount() {
            return Math.ceil(this.pageSize / this.columnCount)
        }
    },
    methods: {
        beforeLeave(el) {
            // Get the current position of the element
            const { top, left } = el.getBoundingClientRect()

            // Set the position to absolute and adjust the top and left properties
            el.style.position = 'absolute'
            el.style.top = `${top}px`
            el.style.left = `${left}px`
        },
        getCellStyles(index) {
            let row = Math.floor(index / this.columnCount)
            let column = index % this.columnCount

            var delay =
                row * this.transitionDelayMultiplier + column * this.transitionDelayMultiplier

            return {
                ...this.cellStyles,
                transitionDelay: `${delay}s`
            }
        },
        setDataLoadSpinner(show, type) {
            // if not done in nextTick, this selects a dom element before it has been added to the dom
            // this may be nothing or it may be the prior route.
            this.$nextTick(() => {
                var el = this.$refs.results
                if (!el) {
                    return
                }
                if (show) {
                    this.$vs.loading({
                        container: el,
                        type: type || 'default'
                    })
                    return
                }
                this.$vs.loading.close(el)
            })
        },
        reset() {
            this.parameterHash = 'initial'
            this.cachedData = {
                initial: {
                    error: false,
                    loading: true,
                    pages: 1,
                    count: 0,
                    data: []
                }
            }
            this.getData(this.parameters)
        },
        resetCurrentPage() {
            var keptCaches = ['initial', this.parameterHash]
            Object.keys(this.cachedData).forEach((cacheKey) => {
                if (!keptCaches.contains(cacheKey)) {
                    delete this.cachedData[cacheKey]
                }
            })
            this.getData(this.parameters, true)
        },
        updateRecord(record) {
            var recordId = this.getKey(record)
            // iterate through each set of cached search data
            // merge the new record into any which share the updated record's id
            Object.keys(this.cachedData).forEach((cacheKey) => {
                this.cachedData[cacheKey].data.forEach((cachedRecord) => {
                    if (this.getKey(cachedRecord) === recordId) {
                        Object.assign(cachedRecord, record)
                    }
                })
            })
        },
        async removeRecord(record) {
            var activeIndex = -1
            if (this.localValue && this.getKey(record) == this.getKey(this.localValue)) {
                activeIndex = this.activeDataSet.data.indexOf(this.localValue)
            }
            // iterate through each set of cached search data
            Object.keys(this.cachedData).forEach((cacheKey) => {
                if (cacheKey != 'initial' && cacheKey != this.parameterHash) {
                    delete this.cachedData[cacheKey]
                }
            })

            await this.getData(this.parameters, true)
            setTimeout(
                function () {
                    if (activeIndex == -1) {
                        return
                    }
                    if (!this.activeDataSet.data.any()) {
                        this.$emit('input', null)
                    }
                    var val = this.activeDataSet.data[activeIndex]
                    if (!val) {
                        val = this.activeDataSet.data[activeIndex - 1]
                    }
                    this.$emit('input', val)
                }.bind(this),
                200
            )
        },
        async generateHash(parameters) {
            return await Object.hash(parameters)
        },
        onSearch(searchText) {
            if (this.searchTimeout != null) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }
            this.setDataLoadSpinner(true, 'sound')
            this.searchTimeout = setTimeout(
                function () {
                    this.searchTimeout = null
                    // If a search handler is bound, allow it to handle
                    // the filter. This allows multiple components to be bound
                    // to the same filter set at a higher level (eg: summaries)
                    if (this.$listeners.search && this.isActive) {
                        this.$emit('search', searchText)
                    }
                    this.searchText = searchText || ''
                    this.setDataLoadSpinner(false)
                }.bind(this),
                this.typingDelay
            )
        },
        retrieveCache(hash) {
            return this.cachedData[hash]
        },
        async getData(params, overwrite) {
            if (!this.isActive) {
                return
            }
            var hash = await this.generateHash(params)
            if (!overwrite && this.retrieveCache(hash)) {
                this.parameterHash = hash
                return
            }
            this.$set(this.cachedData, hash, {
                error: false,
                loading: true,
                count: this.activeDataSet.count,
                pages: this.activeDataSet.pages,
                data: this.setIndexX(this.activeDataSet.data)
            })
            this.parameterHash = hash
            try {
                var response = this.fetchData(params)
                // check if function was async
                if (response.then) {
                    response = await response
                }
                this.$set(this.cachedData, hash, {
                    error: false,
                    count: response.totalCount,
                    pages: response.totalPages,
                    data: this.setIndexX(response.data)
                })
            } catch (err) {
                this.cachedData[hash].error = err
                this.notifyServerError(err)
            } finally {
                this.cachedData[hash].loading = false
            }
        },
        setIndexX(data) {
            if (data.length === 0) {
                return []
            }
            return data.map(function (item, index) {
                return { ...item, xIndex: index + 1 }
            })
        }
    },
    activated() {
        // isActive is defaulted to true, so this will only run when the component has been kept alive
        if (!this.onActiveRoute) {
            this.onActiveRoute = true
        }
    },
    deactivated() {
        this.onActiveRoute = false
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.set-item-move,
.set-item-enter-active {
    transition: opacity 0.25s, transform 0.3s cubic-bezier(0.55, 0, 0.1, 1);
}

/* 2. declare enter from and leave to state */
.set-item-enter,
.set-item-leave-to {
    opacity: 0;
    transform: translateY(-30%);
}

/* 3. ensure leaving items are taken out of layout flow so that moving
animations can be calculated correctly. */
.set-item-leave-active {
    position: absolute;
    transition: none;
    transition-delay: 0 !important;
}

.entry-cell * {
    transition-delay: 0s;
}
</style>
